// src/services/payment.js
import { getFunctions, httpsCallable } from "firebase/functions";

const RAZORPAY_KEY_ID  = "rzp_live_SuGdumZkeZ5KrA";  // TODO: Replace
// const PAYPAL_CLIENT_ID = "YOUR_PAYPAL_CLIENT_ID";  // TODO: Replace

// ── Which global payment provider is active ──────────────────
export const GLOBAL_PROVIDERS = {
  paypal: true,  // set false to hide
  stripe: false,  // set false to hide
};

// ─── RAZORPAY ────────────────────────────────────────────────
export const loadRazorpay = () =>
  new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export const initiateRazorpayPayment = async ({ amount, planName, userEmail, onSuccess, onFailure }) => {
  // TODO: Uncomment when Razorpay key is ready
  
  const loaded = await loadRazorpay();
  if (!loaded) { onFailure("Razorpay SDK failed to load"); return; }
  const options = {
    key: RAZORPAY_KEY_ID,
    amount: amount * 100,
    currency: "INR",
    name: "CreatorComment",
    description: `${planName} Plan`,
    prefill: { email: userEmail },
    theme: { color: "#6f6af8" },
    handler: (response) => onSuccess(response),
    modal: { ondismiss: () => onFailure("Payment cancelled") },
  };
  new window.Razorpay(options).open();
  
  // alert(`[STUB] Razorpay ₹${amount} for ${planName}. Configure payment.js to enable.`);
};

// ─── PAYPAL ──────────────────────────────────────────────────
export const initiatePayPalPayment = async ({ amount, planName, currency = "USD", onSuccess, onFailure }) => {
  // TODO: Uncomment when PayPal client ID is ready
  /*
  ...
  */
  alert(`[STUB] PayPal ${currency} ${amount} for ${planName}. Configure payment.js to enable.`);
};

// ─── PLAN CONFIG ─────────────────────────────────────────────
// Single source of truth — used by Settings, Dashboard, Navbar, History
export const PLANS = {
  free: {
    id: "free",
    name: "Free",
    priceINR: 0,
    priceUSD: 0,
    // Limit: 5 analyses per WEEK (tracked by backend as `remaining` field)
    weeklyLimit: 5,
    monthlyLimit: null,
    historyLimit: 3,           // only last 3 analyses visible
    features: [
      { text: "Analyze up to 5 videos/week",              included: true  },
      { text: "Limited video ideas",                       included: true  },
      { text: "Limited AI reply suggestions",              included: true  },
      { text: "Pin comment suggestions",                   included: true  },
      { text: "Limited missed opportunities detection",    included: true  },
      { text: "Basic comments explorer",                   included: true  },
      { text: "History for last 3 videos",                 included: true  },
      { text: "Standard AI quality",                       included: true  },
      { text: "Standard processing speed",                 included: true  },
      { text: "Multi-video analysis (Coming Soon)",        included: false },
      { text: "Full channel analysis (Coming Soon)",       included: false },
    ],
    cta: "Current Plan",
  },
  pro: {
    id: "pro",
    name: "Pro",
    priceINR: 299,
    priceINRYearly: 2999,
    priceUSD: 4,
    priceUSDYearly: 39,
    weeklyLimit: null,
    monthlyLimit: 100,
    historyLimit: null,        // unlimited history
    
    // razorpayPlanId: "plan_SnD5QpKmEYYYi4", // ← test Razorpay plan ID
    // razorpayYearlyPlanId: "plan_SnD5rfPHtpjciF", // ← test Razorpay yearplan ID    
    razorpayPlanId: "plan_SnCijCIZh5ZKa1", // ← live Razorpay plan ID
    razorpayYearlyPlanId: "plan_SnD0alrhOp80n7", // ← live Razorpay yearplan ID

    // paypalPlanId:   "P-7BD49562DC6808232NH7XAWA",  // ← test PayPal plan ID
    // paypalYearlyPlanId: "P-71E272712A601273NNH7XBVA", // test paypal id
    paypalPlanId:   "P-3JL0863413252110YNH7WPVI",  // ← live PayPal plan ID
    paypalYearlyPlanId: "P-4G005983W0367082JNH7WQLQ", //live paypal id

    // Stripe (price IDs from Stripe dashboard)
    stripePlanId:         "price_1TVPN1AJeZ68JXhmuKYzchik", // test key monthly
    stripeYearlyPlanId:   "price_1TVPNzAJeZ68JXhm7frFQMKw", // test key yearly

    features: [
      { text: "Analyze up to 100 videos/month",           included: true },
      { text: "Get 5 AI video ideas per analysis",        included: true },
      { text: "Unlimited AI reply drafts",                included: true },
      { text: "Pin-worthy comment suggestions",           included: true },
      { text: "Missed opportunities detection",           included: true },
      { text: "Advanced comment explorer",                included: true },
      { text: "Full analysis history",                    included: true },
      { text: "Multi-video analysis (Coming Soon)",       included: true },
      { text: "Full channel analysis (Coming Soon)",      included: true },
      { text: "Premium AI quality",                       included: true },
      { text: "Priority processing speed",                included: true },
    ],
    cta: "Upgrade to Pro",
  },
};

// ── Razorpay SUBSCRIPTION ────────────────────────────────────
export const initiateRazorpaySubscription = async ({ planId, planName, userEmail, userName, onSuccess, onFailure }) => {
  // ← load SDK first
  const loaded = await loadRazorpay();
  if (!loaded) {
    onFailure("Razorpay SDK failed to load");
    return;
  }

  const options = {
    key:             RAZORPAY_KEY_ID,
    subscription_id: planId,
    name:            "CreatorComment",
    description:     `${planName} Plan - Monthly Subscription`,
    prefill: {
      email: userEmail,
      name:  userName,
    },
    theme: { color: "#6f6af8" },
    handler: (response) => onSuccess(response),
    modal: { ondismiss: () => onFailure("Payment cancelled") },
  };

  const rzp = new window.Razorpay(options);
  rzp.on("payment.failed", (response) => onFailure(response.error.description));
  rzp.open();
};

// ── PayPal SUBSCRIPTION ──────────────────────────────────────
export const initiatePayPalSubscription = async ({ planId, planName, cycle, onSuccess, onFailure }) => {
  try {
    const functions  = getFunctions();
    const createSub  = httpsCallable(functions, "createPayPalSubscription");
    const result     = await createSub({ planId, cycle });

    if (result.data.success === false) {
      onFailure(result.data.description || result.data.error);
      return;
    }

    const { approvalUrl, subscriptionId } = result.data;

    if (!approvalUrl) {
      onFailure("No approval URL returned from PayPal");
      return;
    }

    // Save subscriptionId before redirecting
    onSuccess({ approvalUrl, subscriptionId });

  } catch (error) {
    onFailure(error.message);
  }
};

